import { ContentContainer } from "components/ui/display/ContentContainer";
import styled, { useTheme } from "styled-components";
import { CircleMarker, MapContainer, WMSTileLayer, Tooltip, useMapEvents, useMap } from "react-leaflet";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const ProjectsMapContainer = ({projects}) => {

    const fallbackCenter = [60.198856, 8.130057];
    const fallbackZoom = 6;
    const [mapZoom, setMapZoom] = useState(fallbackZoom);

    const currentProjects = projects.filter(prj => prj.projectMap.enabled
                                                    && prj.projectMap.position?.lat
                                                    && prj.projectMap.position?.lng
                                                    && prj.projectMap.displayOnDashboard);

    const hasProjects = currentProjects.length !== 0;


    const mapBounds = hasProjects
                    ? currentProjects.map((p) => p.projectMap.position)
                    : null;

    return (
        <StyledProjectsMapContainer padding="none">
            <div className="mapHeader">
                <MaterialIcon name='map' weight={300} size='sm'/>
                    Prosjektlokasjoner
                <div/>
            </div>
            <div className="mapContent">
                <MapContainer
                    attributionControl={false}
                    style={{ height: "100%", width: "100%"}}
                    center={fallbackCenter}
                    zoom={fallbackZoom}
                >
                    {hasProjects ? <FitBounds bounds={mapBounds}/> : null}
                    <MapZoomListener onZoomChange={setMapZoom} />
                    <WMSTileLayer
                        url="https://wms.geonorge.no/skwms1/wms.topograatone"
                        layers="topograatone"
                        format="image/png"
                        transparent={false}
                        attribution="Kartverket / Geonorge"
                    />
                    {
                        currentProjects.map(prj => <ProjectMarker key={prj.projectUuid} project={prj} zoom={mapZoom}/>)
                    }
                </MapContainer>
            </div>
        </StyledProjectsMapContainer>
    )
}

const StyledProjectsMapContainer = styled(ContentContainer)`
    min-height: 400px;
    max-height: 500px;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        flex: 1 0 0;
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        width: 100%;
    }

    div.mapHeader
    {
        padding: ${({theme}) => theme.spacing.md};
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        display: flex;
        align-items: center;
        gap: ${({theme}) => theme.spacing.md};
        font-weight: 600;

        .icon
        {
            
            color: ${({theme}) => theme.colors.mutedForeground};
        }

        > div
        {
            height: 32px;
        }
    }

    div.mapContent
    {
        flex: 1;
        overflow: hidden;
        border-radius: ${({theme}) => theme.cornerRadius.md};
    }

    .roadNumberTooltip
    {
        border-radius: ${({theme}) => theme.cornerRadius.md};
        filter: drop-shadow(0 2px 2px rgba( ${({theme}) => theme.colors.neutralSecondaryForegroundRGB}, 0.15));
        font-weight: 800;
        padding: ${({theme}) => theme.spacing.xs} ${({theme}) => theme.spacing.md} !important;

        &::before
        {
            display: none;
        }
    }
`


const ProjectMarker = ({project, zoom}) => {

    const [activeMarker, setActiveMarker] = useState(false);
    const GlobalTheme = useTheme();
    const roadNumber = extractRoadNumber(project.projectName);
    const navigate = useNavigate();

    return (
        <CircleMarker
            center={project.projectMap.position}
            radius={7}
            pathOptions={{
                color: GlobalTheme.colors.neutralPrimaryBackground,
                weight: 2,
                fillColor: activeMarker ? GlobalTheme.colors.accentForeground : GlobalTheme.colors.neutralPrimaryForeground,
                fillOpacity: 1
            }}
            eventHandlers={{
                        mouseover: () => {setActiveMarker(true)},
                        mouseout:  () => {setActiveMarker(false)},
                        click: () => { navigate('/projects/' + project.projectUuid)}
                    }}
            
        >
            
            {
                roadNumber && (zoom > 8 || activeMarker) ?
                <Tooltip
                    permanent
                    direction="top"
                    offset={[0, -10]}
                    className='roadNumberTooltip'
                >
                    {roadNumber}
                </Tooltip>
                : null
            }
            
        </CircleMarker>
    )
}

const MapZoomListener = ({ onZoomChange }) => {
    useMapEvents({
        zoomend: (e) => {
            onZoomChange(e.target.getZoom());
        }
    });

    return null;
};

const extractRoadNumber = (name) => {
    if (typeof name !== "string") return null;

    const text = name.trim();

    const europeanRoadRegex = /\b(E)\s?(\d{1,3})\b/;

    const nationalRoadRegex = /\b([Rr][Vv])\.?\s?(\d{1,3})\b/;

    const europeanMatch = text.match(europeanRoadRegex);

    if (europeanMatch) {
        return `${europeanMatch[1]}${europeanMatch[2]}`;
    }

    const nationalMatch = text.match(nationalRoadRegex);

    if (nationalMatch) {
        return `Rv${nationalMatch[2]}`;
    }

    return null;
}

const FitBounds = ({ bounds }) => {
    const map = useMap();
    const initialized = useRef(false);

    useEffect(() => {
        if(bounds && bounds.length > 0 && !initialized.current)
        {
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 8 });
            initialized.current = true;
        }
    }, [bounds, map]);

    return null;
}